import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  doc,
  setDoc,
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

// ============================================
// AI KONFIQURASI
// ============================================
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;

const AI_CONFIG = {
  API_KEY: GROQ_API_KEY,
  API_ENDPOINT: 'https://api.groq.com/openai/v1/chat/completions',
  MODEL: 'llama-3.3-70b-versatile',
  MAX_OUTPUT_TOKENS: 600,
  TEMPERATURE: 0.3
};

// ============================================
// KOD MÜRƏKKƏBLIĞI HESABLAMA
// ============================================
const calculateCodeComplexity = (code) => {
  if (!code) return { score: 0, factor: 1, lines: 0 };
  
  const lines = code.trim().split('\n');
  const totalLines = lines.length;
  const nonEmptyLines = lines.filter(l => l.trim());
  const codeLines = nonEmptyLines.length;
  const commentLines = nonEmptyLines.filter(l => l.trim().startsWith('#')).length;
  const effectiveLines = codeLines - commentLines;
  
  const complexityFactor = 1 + Math.log10(Math.max(1, effectiveLines / 10));
  const difficultyScore = Math.min(100, effectiveLines * 2 + 
    (code.includes('def') ? 10 : 0) + 
    (code.includes('class') ? 15 : 0) +
    (code.includes('import') ? 5 : 0)
  );
  
  return {
    score: Math.round(difficultyScore),
    factor: Math.round(complexityFactor * 100) / 100,
    lines: totalLines,
    effectiveLines: effectiveLines
  };
};

// ============================================
// ÇƏTINLIK SƏVIYYƏSI TƏYIN
// ============================================
const getDifficultyLevel = (score) => {
  if (score < 20) return { level: 'Başlanğıc', color: '#4ade80', icon: '🌱' };
  if (score < 40) return { level: 'Asan', color: '#60a5fa', icon: '🌿' };
  if (score < 60) return { level: 'Orta', color: '#fbbf24', icon: '🌲' };
  if (score < 80) return { level: 'Çətin', color: '#f97316', icon: '🔥' };
  return { level: 'Ekspert', color: '#ef4444', icon: '👑' };
};

const Editor = ({ 
  mode = 'editor', 
  starterCode = 'print("Salam Dünya!")', 
  onCodeRun, 
  userId, 
  userName = 'guest', 
  courseId = 'ai-python', 
  topicId = 1 
}) => {
  const [code, setCode] = useState(starterCode);
  const [output, setOutput] = useState('Sistem başladılır...');
  const [plotUrl, setPlotUrl] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [complexityResult, setComplexityResult] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  const pyodideRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const storageKey = `editor_code_${courseId}_${topicId}_${mode}_${userId || 'guest'}`;

  // Ekran ölçüsünü yoxla
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // LocalStorage
  useEffect(() => {
    const savedCode = localStorage.getItem(storageKey);
    if (savedCode?.trim() && savedCode !== starterCode) {
      setCode(savedCode);
    } else {
      setCode(starterCode);
    }
  }, [starterCode, storageKey]);

  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(storageKey, code);
    }, 1000);
    return () => clearTimeout(saveTimeoutRef.current);
  }, [code, storageKey]);

  // ============================================
  // DATABASE YAZMA
  // ============================================
  const saveAttemptToDatabase = async (userCode, executionOutput, isSuccess, executionTime = 0, errorMsg = null) => {
    if (!userName || userName === 'guest' || !userName.trim()) {
      console.warn('userName uyğun deyil:', userName);
      return;
    }

    if (!db) {
      console.error('Firebase db yoxdur!');
      return;
    }

    setIsSaving(true);
    
    try {
      const now = new Date();
      const timeStr = now.toTimeString().slice(0, 5);
      const dateStr = now.toISOString().split('T')[0];
      const docId = `${timeStr}_${dateStr}_${now.getTime()}`;
      const parentDocId = `${userName}_movzu${topicId}`;
      
      const complexity = calculateCodeComplexity(userCode);
      setComplexityResult(complexity);
      
      const parentRef = doc(db, 'totalCode', parentDocId);
      const parentSnap = await getDoc(parentRef);
      
      const parentData = {
        userName: userName,
        topicId: Number(topicId),
        courseId: courseId,
        updatedAt: serverTimestamp(),
        lastAttempt: {
          timeStr: timeStr,
          dateStr: dateStr,
          isSuccess: isSuccess,
          complexity: complexity.score
        }
      };
      
      if (!parentSnap.exists()) {
        parentData.createdAt = serverTimestamp();
        parentData.totalAttempts = 1;
        parentData.successfulAttempts = isSuccess ? 1 : 0;
        parentData.failedAttempts = isSuccess ? 0 : 1;
        parentData.totalCodeLines = complexity.lines;
      } else {
        const current = parentSnap.data();
        parentData.totalAttempts = (current.totalAttempts || 0) + 1;
        parentData.successfulAttempts = (current.successfulAttempts || 0) + (isSuccess ? 1 : 0);
        parentData.failedAttempts = (current.failedAttempts || 0) + (isSuccess ? 0 : 1);
        parentData.totalCodeLines = (current.totalCodeLines || 0) + complexity.lines;
      }
      
      await setDoc(parentRef, parentData, { merge: true });

      const attemptRef = doc(db, 'totalCode', parentDocId, 'attempts', docId);
      await setDoc(attemptRef, {
        timeStr: timeStr,
        dateStr: dateStr,
        timestamp: serverTimestamp(),
        isSuccess: isSuccess,
        code: userCode || '',
        output: executionOutput || '',
        complexity: complexity,
        error: errorMsg ? { message: errorMsg, type: errorMsg.split(':')[0] || 'Unknown' } : null
      });
      
    } catch (err) {
      console.error('Database xətası:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // AI ANALIZ
  // ============================================
  const analyzeErrorWithAI = async (userCode, errorOutput) => {
    if (!AI_CONFIG.API_KEY) {
      setAiAnalysis({ text: "⚠️ API Key tapılmadı", error: true });
      return;
    }

    setAiLoading(true);
    
    try {
      const systemMessage = `Sən Python müəllimisisən. Xətanı analiz et və azərbaycanca izah et. Sadə dildə, konkret olaraq hansı sətirdə problem olduğunu göstər.`;
      const userPrompt = `Kod:\n${userCode?.slice(0, 800)}\n\nXəta:\n${errorOutput?.slice(0, 500)}`;

      const response = await fetch(AI_CONFIG.API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AI_CONFIG.API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: AI_CONFIG.MODEL,
          messages: [
            { role: 'system', content: systemMessage },
            { role: 'user', content: userPrompt }
          ],
          temperature: AI_CONFIG.TEMPERATURE,
          max_tokens: AI_CONFIG.MAX_OUTPUT_TOKENS
        })
      });

      if (!response.ok) throw new Error(`API ${response.status}`);

      const result = await response.json();
      
      setAiAnalysis({
        text: result.choices[0].message.content,
        timestamp: new Date().toLocaleTimeString(),
        type: 'error_fix'
      });
      
    } catch (err) {
      setAiAnalysis({ 
        text: `⚠️ AI analizi alınmadı: ${err.message}`, 
        error: true 
      });
    } finally {
      setAiLoading(false);
    }
  };

  // ============================================
  // FAYL YÜKLƏMƏ
  // ============================================
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !isReady) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const content = event.target.result;
        pyodideRef.current.FS.writeFile(file.name, content);
        setCode(prev => prev + '\n' + content);
        setOutput(`✅ '${file.name}' yükləndi və editörə əlavə edildi.`);
      } catch (err) { 
        setOutput("❌ Fayl xətası: " + err.message); 
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Pyodide init
  useEffect(() => {
    const initPython = async () => {
      try {
        if (!window.loadPyodide) {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/pyodide.js';
          script.async = true;
          document.head.appendChild(script);
          await new Promise((res, rej) => {
            script.onload = res;
            script.onerror = () => rej(new Error('Pyodide yüklənmədi'));
          });
        }
        pyodideRef.current = await window.loadPyodide();
        await pyodideRef.current.loadPackage("micropip");
        setIsReady(true);
        setOutput('✅ Sistem hazırdır. Kodunuzu yazın və RUN düyməsinə basın.');
      } catch (err) { 
        setOutput("❌ Xəta: " + err.message); 
      }
    };
    initPython();
  }, []);

  const clearCode = () => {
    setCode('');
    setOutput('Kod təmizləndi.');
    setPlotUrl(null);
    setAiAnalysis(null);
    setComplexityResult(null);
    localStorage.removeItem(storageKey);
  };

  const runCode = useCallback(async () => {
    if (!isReady) {
      setOutput('⏳ Sistem hazırlanır...');
      return;
    }

    const startTime = Date.now();
    setOutput('🔄 İcra olunur...\n');
    setPlotUrl(null);
    setAiAnalysis(null);
    setComplexityResult(null);
    
    try {
      const packagesToLoad = [];
      if (code.includes('matplotlib')) packagesToLoad.push('matplotlib');
      if (code.includes('numpy')) packagesToLoad.push('numpy');
      if (code.includes('pandas')) packagesToLoad.push('pandas');

      for (const pkg of packagesToLoad) {
        setOutput(prev => prev + `📦 ${pkg}...\n`);
        await pyodideRef.current.loadPackage(pkg);
      }

      if (code.includes('seaborn')) {
        const micropip = pyodideRef.current.pyimport("micropip");
        await micropip.install("seaborn");
      }

      await pyodideRef.current.runPythonAsync(`
import sys, io, base64
sys.stdout = io.StringIO()
import builtins
builtins.input = lambda msg="": str(__import__('js').prompt(msg) or "")

def get_plot():
    try:
        import matplotlib.pyplot as plt
        if plt.get_fignums():
            buf = io.BytesIO()
            plt.savefig(buf, format='png')
            return base64.b64encode(buf.getvalue()).decode()
    except: 
        pass
    return None
      `);

      await pyodideRef.current.runPythonAsync(code);
      const stdout = pyodideRef.current.runPython("sys.stdout.getvalue()");
      const imgData = pyodideRef.current.runPython("get_plot()");
      
      const executionTime = Date.now() - startTime;
      const finalOutput = stdout || "✅ İcra olundu.";
      setOutput(finalOutput);
      if (imgData) setPlotUrl("data:image/png;base64," + imgData);
      
      await saveAttemptToDatabase(code, finalOutput, true, executionTime, null);
      
      if (onCodeRun) onCodeRun(finalOutput, null);
      
    } catch (err) {
      const executionTime = Date.now() - startTime;
      const errorMsg = err.message;
      const errorOutput = "❌ XƏTA:\n" + errorMsg;
      setOutput(errorOutput);
      
      await analyzeErrorWithAI(code, errorMsg);
      await saveAttemptToDatabase(code, errorOutput, false, executionTime, errorMsg);
      
      if (onCodeRun) onCodeRun(errorOutput, err);
    }
  }, [code, isReady, onCodeRun, userName, topicId]);

  // Çətinlik səviyyəsi komponenti
  const ComplexityBadge = () => {
    if (!complexityResult) return null;
    const diff = getDifficultyLevel(complexityResult.score);
    
    return (
      <div style={{
        marginTop: '12px',
        padding: '12px',
        background: '#1a1a2e',
        borderRadius: '8px',
        border: `2px solid ${diff.color}`,
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          marginBottom: '8px'
        }}>
          <span style={{ fontSize: '20px' }}>{diff.icon}</span>
          <span style={{ 
            color: diff.color, 
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            Çətinlik Səviyyəsi: {diff.level}
          </span>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: '#888' }}>Mürəkkəblik balı:</span>
            <span style={{ color: '#fff' }}>{complexityResult.score}/100</span>
          </div>
          
          <div style={{ 
            width: '100%', 
            height: '6px', 
            background: '#333',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${complexityResult.score}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${diff.color}, ${diff.color}88)`,
              transition: 'width 0.5s ease'
            }} />
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            fontSize: '11px',
            color: '#666',
            marginTop: '4px'
          }}>
            <span>{complexityResult.effectiveLines} effektiv sətir</span>
            <span>{complexityResult.lines} ümumi sətir</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: isMobile ? '100vh' : mode === 'exercise' ? '700px' : '800px',
      maxHeight: '900px',
      background: '#1e1e1e', 
      borderRadius: '10px', 
      border: '1px solid #444', 
      overflow: 'hidden',
      fontFamily: 'monospace'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '10px 15px', 
        background: '#2d2d2d', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #444',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ 
            color: isReady ? '#00ff00' : '#ffaa00', 
            fontSize: '12px', 
            fontWeight: 'bold'
          }}>
            {isReady ? '●' : '◐'} PYTHON {mode === 'exercise' ? 'EXERCISE' : 'AI LAB'}
          </span>
          
          {userName && userName !== 'guest' && (
            <span style={{ 
              color: '#888', 
              fontSize: '11px', 
              background: '#1a1a1a', 
              padding: '2px 8px', 
              borderRadius: '4px' 
            }}>
              👤 {userName}
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Gizli fayl input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".py,.txt"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            disabled={!isReady}
          />
          
          <button 
            onClick={triggerFileUpload}
            disabled={!isReady}
            style={{ 
              background: '#3a3a3a', 
              color: isReady ? '#4a9eff' : '#666', 
              border: '1px solid #555', 
              padding: '6px 12px', 
              cursor: isReady ? 'pointer' : 'not-allowed', 
              borderRadius: '4px', 
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Python faylı yüklə"
          >
            📁 Yüklə
          </button>
          
          {isSaving && <span style={{ color: '#888', fontSize: '11px' }}>💾 ...</span>}
          
          <button 
            onClick={clearCode} 
            disabled={!isReady}
            style={{ 
              background: '#444', 
              color: 'white', 
              border: 'none', 
              padding: '6px 12px', 
              cursor: isReady ? 'pointer' : 'not-allowed', 
              borderRadius: '4px', 
              fontSize: '12px'
            }}>
            🗑️ TƏMİZLƏ
          </button>
          
          <button 
            onClick={runCode} 
            disabled={!isReady}
            style={{ 
              background: isReady ? '#2ea44f' : '#555', 
              color: 'white', 
              border: 'none', 
              padding: '6px 16px', 
              cursor: isReady ? 'pointer' : 'not-allowed', 
              borderRadius: '4px', 
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
            ▶ RUN
          </button>
        </div>
      </div>

      {/* Main Content - Responsive Layout */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row', 
        flex: 1, 
        minHeight: 0,
        overflow: 'hidden'
      }}>
        {/* Kod yazan hissə */}
        <div style={{ 
          flex: 1,
          minHeight: isMobile ? '250px' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          borderRight: isMobile ? 'none' : '1px solid #333',
          borderBottom: isMobile ? '2px solid #444' : 'none'
        }}>
          <div style={{
            padding: '8px 12px',
            background: '#252525',
            color: '#888',
            fontSize: '11px',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span>✏️ Kod Editörü</span>
            <span style={{ color: '#666' }}>{code.split('\n').length} sətir</span>
          </div>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={isReady ? '# Python kodunuzu bura yazın...' : 'Sistem hazırlanır...'}
            style={{ 
              flex: 1,
              background: '#1e1e1e', 
              color: '#d4d4d4', 
              padding: '12px', 
              border: 'none', 
              outline: 'none', 
              resize: 'none', 
              fontFamily: 'monospace', 
              fontSize: isMobile ? '13px' : '14px',
              lineHeight: '1.5',
              minHeight: isMobile ? '200px' : 'auto'
            }}
            spellCheck="false"
            disabled={!isReady}
          />
        </div>

        {/* Output hissəsi */}
        <div style={{ 
          flex: 1,
          minHeight: isMobile ? '250px' : 'auto',
          background: '#000', 
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '8px 12px',
            background: '#1a1a1a',
            color: '#888',
            fontSize: '11px',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span>🖥️ Output</span>
            <span style={{ color: isReady ? '#4ade80' : '#fbbf24' }}>
              {isReady ? '✅ Hazır' : '⏳ Yüklənir...'}
            </span>
          </div>
          
          <div style={{
            flex: 1,
            padding: '12px',
            overflowY: 'auto'
          }}>
            <pre style={{ 
              color: output.includes('❌') ? '#ff6b6b' : '#00ff00', 
              fontSize: isMobile ? '12px' : '13px',
              whiteSpace: 'pre-wrap',
              margin: '0 0 12px 0',
              lineHeight: '1.4'
            }}>
              {output}
            </pre>
            
            {plotUrl && (
              <img 
                src={plotUrl} 
                alt="Plot" 
                style={{ 
                  maxWidth: '100%', 
                  background: 'white', 
                  borderRadius: '4px',
                  marginTop: '10px'
                }} 
              />
            )}

            {/* Çətinlik səviyyəsi */}
            <ComplexityBadge />

            {aiLoading && (
              <div style={{ color: '#a0a0ff', marginTop: '12px', fontSize: '12px' }}>
                🤖 AI analiz edir...
              </div>
            )}

            {aiAnalysis && !aiLoading && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: aiAnalysis.error ? '#2a2a1a' : '#2a1a1a',
                borderRadius: '6px',
                border: `1px solid ${aiAnalysis.error ? '#ffa500' : '#6a4a4a'}`,
                color: '#e0e0e0'
              }}>
                <div style={{ 
                  color: aiAnalysis.error ? '#ffa500' : '#ffa0a0', 
                  fontSize: '12px', 
                  fontWeight: 'bold',
                  marginBottom: '6px'
                }}>
                  {aiAnalysis.error ? '⚠️ AI Xəta' : '🔴 AI Təhlili'}
                </div>
                <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                  {aiAnalysis.text}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Status Bar */}
      <div style={{
        padding: '8px 15px',
        background: '#2d2d2d',
        borderTop: '1px solid #444',
        fontSize: '11px',
        color: '#888',
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '5px'
      }}>
        <span>{code.length} simvol</span>
        <span>
          {isSaving ? '💾 Saxlanılır...' : 
           aiLoading ? '🤖 AI analiz edir...' : 
           complexityResult ? `📊 Mürəkkəblik: ${complexityResult.score}/100` : '✅ Hazır'}
        </span>
      </div>
    </div>
  );
};

export default Editor;